import { IOpportunitySummary } from '@monetize/types/app';
import { createColumnHelper } from '../../components/Monetize/DataTableNew/appTable';
import { MText } from '../../components/Monetize';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  currencyBodyTemplate,
  dateTimeBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  nameWithIdBodyTemplate,
  textBodyTemplate,
} from '../../components/Monetize/DataTableNew/tableUtilsNew';
import { AccountLookupPopover } from '../../components/Monetize/LookupPopover';
import {
  getQuoteEditRoute,
  getSalesOpportunityEditRoute,
} from '../../constants/routes';

const columnHelper = createColumnHelper<IOpportunitySummary>();

export type OpportunityListColumnContext =
  ColumnContextBase<IOpportunitySummary>;

/** tableId: FilterStateKeys.OPPORTUNITY_LIST */
export const OPPORTUNITY_LIST_CATALOG: ColumnCatalog<
  IOpportunitySummary,
  OpportunityListColumnContext
> = [
  {
    id: 'name',
    label: 'Name',
    customizable: false,
    build: () =>
      columnHelper.accessor('name', {
        ...getColHeaderFilters('TEXT'),
        cell: nameWithIdBodyTemplate<IOpportunitySummary, unknown>({
          idProp: 'id',
          nameProp: 'name',
          idLinkFn: (id) => getSalesOpportunityEditRoute(id),
        }),
        enableSorting: true,
      }),
  },
  {
    id: 'accountId',
    label: 'Account',
    customizable: false,
    build: () =>
      columnHelper.accessor('accountId', {
        ...getColHeaderWithSize('Account', 220),
        ...getColHeaderFilters('RECORD_SEARCH', {
          filterKey: 'accountId',
          recordSearch: {
            endpoint: '/api/accounts',
            itemTitle: 'accountName',
            itemValue: 'id',
            label: 'Filter by Account',
            sort: 'accountName',
          },
        }),
        enableSorting: false,
        cell: ({ row }) => {
          const data = row.original;
          if (row.original.accountName && row.original.accountId) {
            return (
              <AccountLookupPopover
                displayLabel={row.original.accountName}
                value={row.original.accountId}
              />
            );
          }
          return <MText lineClamp={1}>{data.accountName || '-'}</MText>;
        },
      }),
  },
  {
    id: 'primaryQuoteId',
    label: 'Primary Quote',
    build: () =>
      columnHelper.accessor((row) => row.primaryQuote?.id, {
        id: 'primaryQuoteId',
        ...getColHeaderWithSize('Primary Quote', 400),
        ...getColHeaderFilters('RECORD_SEARCH', {
          filterKey: 'primaryQuoteId',
          recordSearch: {
            endpoint: '/api/quotes',
            itemTitle: 'description',
            itemValue: 'id',
            label: 'Filter by Primary Quote',
            sort: 'description',
          },
        }),
        cell: nameWithIdBodyTemplate<any, unknown>({
          idProp: 'primaryQuote.id',
          nameProp: 'primaryQuote.name',
          idLinkFn: (id) => getQuoteEditRoute(id),
        }),
        enableSorting: true,
      }),
  },
  {
    id: 'amount',
    label: 'Amount',
    build: () =>
      columnHelper.accessor('amount', {
        header: 'Amount',
        ...getColHeaderFilters('NUMBER'),
        cell: currencyBodyTemplate<IOpportunitySummary, unknown>({
          currencyProperty: 'currency',
        }),
        enableSorting: true,
      }),
  },
  {
    id: 'customId',
    label: 'Custom ID',
    build: () =>
      columnHelper.accessor('customId', {
        ...getColHeaderWithSize('Custom ID', 200),
        ...getColHeaderFilters('TEXT'),
        cell: textBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'modifyDate',
    label: 'Updated',
    build: () =>
      columnHelper.accessor('modifyDate', {
        ...getColHeaderWithSize('Updated', 170),
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'createDate',
    label: 'Created',
    build: () =>
      columnHelper.accessor('createDate', {
        ...getColHeaderWithSize('Created', 170),
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
];
